import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputComponent, LoaderComponent, ModalComponent, Text } from "@iiht/components";
import { useTranslation } from "react-i18next";
import { Form, Row, Col } from "antd";
import { addVmDuration, setAddVmDurationLoader } from "../../store/actions";
import { fullSpan, halfResponsiveSpan } from "../../constants/gridTypes";
import { toastNotification } from "../../utils/toast";
import { toasterFlags } from "../../constants/componentConstants";
import { Required } from "../../utils/rules";
import AsteriskSymbol from "../../components/common/AsteriskSymbol";

const useAddDuration = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // form setup
  const [form] = Form.useForm();
  const { setFieldsValue } = form;

  useEffect(() => {
    return () => {
      //clear previous values on popup load
      clearFormFields();
    };
  }, []);

  // get api response from reducers
  const { addDurationLoader } = useSelector(({ myLabsReducer }) => myLabsReducer);
  // hook states
  const [record, setRecord] = useState({});
  const [showAddDurationModal, setShowAddDurationModal] = useState(false);

  // submit handler
  const handleAddDurationOk = () => {
    const fieldValues = form.getFieldValue(); //get field values from form

    if (fieldValues?.durationInMins < 0 || fieldValues?.validityInDays < 0) {
      toastNotification(toasterFlags.ERROR, t("validations.durationErrorMsg"));
      return;
    }
    let payload = {
      vm_id: record?.id,
      validity_days: fieldValues?.validityInDays,
      duration_mins: fieldValues?.durationInMins,
    };
    //clear form fields
    clearFormFields();

    dispatch(setAddVmDurationLoader(true)); //dispatch loader action
    dispatch(addVmDuration(payload)); //dispatch add-on api req
    setShowAddDurationModal(false); //hide duration modal
  };

  const handleAddDurationCancel = () => {
    clearFormFields();
    setShowAddDurationModal(false);
  };

  //clear form fields
  const clearFormFields = () => {
    setFieldsValue({ durationInMins: undefined, validityInDays: undefined });
  };

  const handleAddDuration = (record) => {
    setRecord(record);
    setShowAddDurationModal(true);
  };

  const addDurationDom = (
    <>
      {showAddDurationModal && (
        <ModalComponent visible={showAddDurationModal} handleCancel={handleAddDurationCancel} handleOk={handleAddDurationOk} closable>
          <ModalComponent.H1>
            <Text className="sub-heading-text-two" strong>
              {t("myLabs.addDuration")}
            </Text>
          </ModalComponent.H1>

          <Form form={form} layout={"vertical"} hideRequiredMark onFinish={handleAddDurationOk}>
            <Row gutter={[16, 32]} className="mb2">
              <Col className="gutter-row" span={24}>
                <Form.Item
                  label={
                    <>
                      {t("myLabs.validityInDay")}
                      <AsteriskSymbol />
                    </>
                  }
                  name="validityInDays"
                  rules={[Required(`${t("validations.enter")} ${t("myLabs.validityInDay")}`)]}
                >
                  <InputComponent type="number" min={0} placeholder={t("validations.enterValidity")} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 24]} className="mb2">
              <Col {...fullSpan}>
                <Form.Item
                  label={
                    <>
                      {t("myLabs.DurationInMins")}
                      <AsteriskSymbol />
                    </>
                  }
                  name="durationInMins"
                  rules={[Required(`${t("validations.enter")} ${t("myLabs.DurationInMins")}`)]}
                >
                  <InputComponent type="number" min={0} placeholder={t("validations.enterDuration")} />
                </Form.Item>
              </Col>
              <hr />
            </Row>
            <Row gutter={[16, 48]} className="mt1">
              <Col {...halfResponsiveSpan}>
                <Form.Item>
                  <ModalComponent.Cancel onClick={handleAddDurationCancel}>
                    <Text className="body-short-text" strong>
                      {t("common.cancelText")}
                    </Text>
                  </ModalComponent.Cancel>
                </Form.Item>
              </Col>
              <Col {...halfResponsiveSpan}>
                <Form.Item>
                  <ModalComponent.Proceed htmlType="submit">
                    <Text className="body-short-text text-white" strong>
                      {t("common.add")}
                    </Text>
                  </ModalComponent.Proceed>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </ModalComponent>
      )}
      {addDurationLoader && <LoaderComponent spinning isBackdrop />}
    </>
  );

  return { handleAddDuration, addDurationDom };
};

export default useAddDuration;
